import { Button } from '@/isomorphic/components/typography/button/button';
import { useLocalization } from '@/hooks/use-localization';
import { DecisionResultType, StepCommonProps } from '../types';
import { useBrandedColors } from '../hooks/use-branded-colors';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useLogout } from '@/hooks/use-logout';

export type ApprovedApplicationProps = StepCommonProps & {
    result: DecisionResultType;
    onFinish: () => void;
};

export const ApprovedApplication = ({ result, getLabel, brandColor, onFinish }: ApprovedApplicationProps) => {
    const { formatCurrency } = useLocalization();
    const { useBrandedColorsBool } = useBrandedColors({ brandColor });
    const { logout } = useLogout();

    useEffect(() => {
        return () => {
            logout();
        };
    }, []);

    const {
        approvedAmount,
        interest,
        effectiveInterest,
        tenor,
        monthlyCost,
        totalRepaymentAmount,
        totalFeesAndInterest,
        adminFee,
        arrangementFee,
        consolidationDemand,
    } = result;

    return (
        <div className="r-relative r-mx-auto r-mb-8 r-max-w-2xl r-space-y-4">
            <div
                className={clsx('r-space-y-8 r-rounded-xl r-border-2 r-bg-white r-p-6', useBrandedColorsBool ? '' : 'r-border-primary-200')}
                style={useBrandedColorsBool ? { borderColor: brandColor } : {}}
            >
                <div className="r-flex r-flex-col r-items-center r-space-y-2 r-text-center">
                    <div className="r-mb-4 r-mt-2 r-flex r-h-20 r-w-20 r-items-center r-justify-center r-rounded-full r-bg-success">
                        <img src="/assets/icons/check.svg" className="r-w-10" />
                    </div>
                    <h2 className={clsx('r-text-3xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('approvedTitle')}
                    </h2>
                    <p className="r-text-xl r-font-bold">{formatCurrency(approvedAmount)}</p>
                    <p className="r-text-lg">
                        {getLabel('approvedMonthlyCost')}: {formatCurrency(monthlyCost)}
                    </p>
                    <p className="r-text-gray-600">
                        {getLabel('approvedEffectiveInterest')}: {effectiveInterest}%
                    </p>
                </div>

                <div className="r-space-y-2">
                    <h3 className="r-text-xl r-font-semibold">{getLabel('approvedLoanDetailsTitle')}</h3>
                    <div className="r-w-full r-text-sm">
                        <div className="r-divide-y r-divide-gray-200">
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedTenor')}</span>
                                <span className="r-text-right r-font-bold">
                                    {tenor} {getLabel('months')}
                                </span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedNominalInterest')}</span>
                                <span className="r-text-right r-font-bold">{interest}%</span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedEffectiveInterestDetail')}</span>
                                <span className="r-text-right r-font-bold">{effectiveInterest}%</span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedTotalRepayment')}</span>
                                <span className="r-text-right r-font-bold">{formatCurrency(totalRepaymentAmount)}</span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedFeesAndInterest')}</span>
                                <span className="r-text-right r-font-bold">{formatCurrency(totalFeesAndInterest)}</span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedAdminFee')}</span>
                                <span className="r-text-right r-font-bold">{formatCurrency(adminFee)}</span>
                            </div>
                            <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                <span>{getLabel('approvedArrangementFee')}</span>
                                <span className="r-text-right r-font-bold">{formatCurrency(arrangementFee)}</span>
                            </div>
                            {consolidationDemand > 0 && (
                                <div className="r-flex r-w-full r-justify-between r-gap-4 r-border-b r-border-gray-200 r-py-2">
                                    <span>{getLabel('approvedConsolidationDemand')}</span>
                                    <span className="r-text-right r-font-bold">{formatCurrency(consolidationDemand)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="r-mx-auto r-flex r-max-w-xs r-flex-col r-gap-4">
                    <Button
                        fullWidth
                        to={'/'}
                        onClick={onFinish}
                        brandColor={brandColor}
                        className={clsx('r-text-white', useBrandedColorsBool ? '' : 'r-bg-primary-500')}
                    >
                        {getLabel('landingPageButton')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
